import { AfterViewInit, Component, ElementRef, forwardRef, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { MODAL_SERVICE } from '@base/services/private/modal-service.token';
import { GlobalStateService } from '@base/services/private/global-state.service';
import * as QRCode from 'qrcode';
import { GlobalService } from '@base/services/private/global.service';

@Component({
  selector: 'app-modal-download-app',
  imports: [forwardRef(() => TranslatePipe), ModalHeaderComponent, ModalBodyComponent],
  templateUrl: './modal-download-app.component.html',
  styleUrl: './modal-download-app.component.scss',
})
export class ModalDownloadAppComponent implements AfterViewInit, OnDestroy {
  @ViewChild('qrAndroidApps') qrAndroidApps!: ElementRef<HTMLCanvasElement>;
  @ViewChild('qrIOSApps') qrIOSApps!: ElementRef<HTMLCanvasElement>;

  private readonly modalSrv = inject(MODAL_SERVICE);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly globalSrv = inject(GlobalService);

  @Input() modalId = '';

  /**
   * Lifecycle hook called after the component's view has been fully initialized.
   * Initializes the QR codes for iOS and Android app download links.
   * @returns void
   */
  ngAfterViewInit(): void {
    this.initQrCode();
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Closes the active modal instance.
   *
   * @remarks Called when the user dismisses the modal.
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Opens the iOS app download link in a new browser tab.
   * @returns void
   */
  openLinkIOS(): void {
    const iosLink = this.globalStateSrv.getIosLink();
    window.open(iosLink, '_blank', 'noopener');
  }

  /**
   * Opens the Android app download link in a new browser tab.
   * @returns void
   */
  openLinkAndroid(): void {
    const androidLink = this.globalStateSrv.getAndroidLink();
    window.open(androidLink, '_blank', 'noopener');
  }

  /**
   * Initializes the QR codes for iOS and Android app download links on their respective canvas elements.
   * @returns void
   */
  initQrCode(): void {
    const canvasIOS = this.qrIOSApps.nativeElement;
    const canvasAndroid = this.qrAndroidApps.nativeElement;
    const iosLink = this.globalStateSrv.getIosLink();
    const androidLink = this.globalStateSrv.getAndroidLink();

    // Clear previous drawings
    const contextIOS = canvasIOS.getContext('2d');
    const contextAndroid = canvasAndroid.getContext('2d');
    if (contextIOS) {
      contextIOS.clearRect(0, 0, canvasIOS.width, canvasIOS.height);
    }
    if (contextAndroid) {
      contextAndroid.clearRect(0, 0, canvasAndroid.width, canvasAndroid.height);
    }

    // Draw new QR codes
    QRCode.toCanvas(canvasIOS, iosLink, { width: 300 }, (error) => {
      if (error) console.error('QR Code render error for iOS:', error);
    });
    QRCode.toCanvas(canvasAndroid, androidLink, { width: 300 }, (error) => {
      if (error) console.error('QR Code render error for Android:', error);
    });
  }
}
